import { COMPONENT_QUERY } from '../../../constants/api';
import { FormatLongNumberWithCurrency, toTitleCase } from '../../../constants/utils';
import {
  LEGENDS_HIGHLIGHT_TEXT,
  MONTHLY_OPPS_SEASONALITY,
  MONTHLY_SPENDS_SEASONALITY,
  OPPS_DATA,
  SPEND_AND_OPPS_FILE_NAME_LOCAL_SPEND_MONTHLY,
  SPEND_AND_OPPS_FILE_NAME_LOCAL_SPEND_WEEKLY,
  SPEND_AND_OPPS_FILE_NAME_MONTHLY,
  SPEND_AND_OPPS_FILE_NAME_WEEKLY,
  WEEKLY_OPPS_SEASONALITY,
  WEEKLY_SPENDS_SEASONALITY,
  fiscalMonths,
} from '../constants';
import api from '../../../services/api';
import { formatNumberUsingUnit } from '../../../utils/formatUtils';
import { Typography } from '@mui/material';

const typeOfTable = {
  opps_data: ['actual_opps', 'planned_opps'],
  spends_data: ['actual_spend', 'planned_spend'],
};
export async function getSpendsAndOppsData(payload, key, dataType) {
  let url = COMPONENT_QUERY + '?component=' + key;
  if (dataType) {
    url += '&dataType=' + dataType;
  }
  const response = await api.post(url, payload);
  return response.data;
}

export function transformHeaders(data, store) {
  // Access the results array directly from your data structure.
  const dataHeaders = data.result;
  let headerNameArr = [];
  const filteredData = dataHeaders?.filter((data) => data.store_id === store);

  if (!filteredData.length) return [];

  const uniqueHeaders = new Set();

  filteredData
    .sort((a, b) => a.fiscal_month - b.fiscal_month || a.fiscal_week - b.fiscal_week)
    .forEach((eachHeader) => {
      if (eachHeader.fiscal_month !== null && eachHeader.fiscal_month !== undefined) {
        const headerValue = `${fiscalMonths[eachHeader.fiscal_month]} W${eachHeader.fiscal_week}`;
        const accessorValue = `${fiscalMonths[eachHeader.fiscal_month]}_W${eachHeader.fiscal_week}`;

        // Check if the header is already added to avoid duplicates.
        if (!uniqueHeaders.has(headerValue)) {
          headerNameArr.push({
            Header: headerValue,
            accessor: accessorValue,
          });
          uniqueHeaders.add(headerValue);
        }
      }
    });

  return headerNameArr;
}

export function transformWeeklyData(data, type, store, currentYear, isDownloadData = false) {
  const segregatedData = {};
  const temp_key = typeOfTable[type];
  const filteredData = data?.result.filter((data) => data.store_id === store);

  if (!filteredData.length) return {};
  const sortArr = filteredData
    .slice()
    .sort((a, b) => b.fiscal_year - a.fiscal_year || a.fiscal_week - b.fiscal_week);

  const seasonalityKey = type === OPPS_DATA ? WEEKLY_OPPS_SEASONALITY : WEEKLY_SPENDS_SEASONALITY;

  for (const entry of sortArr) {
    if (entry.fiscal_year <= currentYear) {
      for (const key of temp_key) {
        const isCurrentYear = entry.fiscal_year === currentYear;
        const isPlannedData = key.includes('planned');
        if (!isCurrentYear && isPlannedData && !isDownloadData) {
          continue;
        }

        const data_key_prefix = `${entry.fiscal_year}_${key}`;
        const data_key = `${data_key_prefix}`;

        if (fiscalMonths[entry.fiscal_month]) {
          if (!segregatedData[data_key]) {
            segregatedData[data_key] = {
              [seasonalityKey]: toTitleCase(data_key.split('_').join(' ')),
            };
          }
          if (segregatedData[data_key]) {
            let value;
            if (type === OPPS_DATA) {
              value = entry[key];
            } else if (isDownloadData) {
              value = formatNumberUsingUnit(entry[key], '$');
            } else {
              value = FormatLongNumberWithCurrency(entry[key]);
            }
            segregatedData[data_key][`${fiscalMonths[entry.fiscal_month]}_W${entry.fiscal_week}`] =
              entry[key] !== null ? value : 'N/A';
          }
        }
      }
    }
  }

  const entries = Object.entries(segregatedData);
  const index = entries?.findIndex(
    ([key, value]) =>
      value?.[seasonalityKey] === `${currentYear} Planned ${type === OPPS_DATA ? 'Opps' : 'Spend'}`
  );
  if (entries && index !== -1) {
    const [movedEntry] = entries.splice(index, 1);
    entries.unshift(movedEntry);
  }

  return Object.fromEntries(entries);
}

export const transformMonthlyData = (data, type, store, currentYear, isDownloadData = false) => {
  const segregatedData = {};
  const rowData = [];
  const tableHeadersSet = new Set();
  const temp_key = typeOfTable[type];
  const filteredData = data?.result.filter((data) => data.store_id === store);
  if (!filteredData.length) return { formattedRowData: [], tableHeaders: [] };
  const sortArr = filteredData.slice().sort((a, b) => a.fiscal_month - b.fiscal_month);

  const seasonalityKey = type === OPPS_DATA ? MONTHLY_OPPS_SEASONALITY : MONTHLY_SPENDS_SEASONALITY;

  for (const entry of sortArr) {
    for (const key of temp_key) {
      const data_key = `${entry.fiscal_year}_${key}`;
      if (fiscalMonths[entry.fiscal_month]) {
        if (!segregatedData[data_key]) {
          if (
            data_key.includes(`${currentYear}_actual`) ||
            data_key.includes(`${currentYear}_planned`) ||
            (!data_key.includes(`${currentYear}`) &&
              !data_key.includes(type === OPPS_DATA ? `planned_opps` : `planned_spend`)) ||
            isDownloadData
          ) {
            segregatedData[data_key] = {
              [seasonalityKey]: toTitleCase(data_key.split('_').join(' ')),
            };
          }
        }
        if (segregatedData[data_key]) {
          let value;
          if (type === OPPS_DATA) {
            value = entry[key];
          } else if (isDownloadData) {
            value = formatNumberUsingUnit(entry[key], '$');
          } else {
            value = FormatLongNumberWithCurrency(entry[key]);
          }
          segregatedData[data_key][`${fiscalMonths[entry.fiscal_month]}`] =
            entry[key] !== null ? value : 'N/A';
        }
        tableHeadersSet.add(`${fiscalMonths[entry.fiscal_month]}`);
      }
    }
  }

  const tableHeaders = [
    {
      Header: () => (
        <span style={{ width: '200px', display: 'inline-block' }}>
          {' '}
          {type === OPPS_DATA ? 'Monthly Opps Seasonality' : 'Monthly Spends Seasonality'}
        </span>
      ),
      accessor: seasonalityKey,
    },
    ...Array.from(tableHeadersSet).map((header) => ({
      Header: header.split('_').join(' '),
      accessor: header,
    })),
  ];

  for (const key in segregatedData) {
    const entry = segregatedData[key];
    for (const header of tableHeaders) {
      const accessor = header.accessor;
      if (entry[accessor] === 0) {
        entry[accessor] = 0;
      } else if (!entry[accessor]) {
        entry[accessor] = 'N/A';
      }
    }
    rowData.push(segregatedData[key]);
  }

  rowData?.sort(
    (a, b) => b?.[seasonalityKey]?.split(' ')?.[0] - a?.[seasonalityKey]?.split(' ')?.[0]
  );
  const index = rowData?.findIndex(
    (item) =>
      item?.[seasonalityKey] === `${currentYear} Planned ${type === OPPS_DATA ? 'Opps' : 'Spend'}`
  );
  if (index !== -1) {
    const [movedItem] = rowData.splice(index, 1);
    rowData.unshift(movedItem);
  }

  return { rowData, tableHeaders };
};

export const getOTPayload = (key, selectedStore, selectedYears, channels = null) => {
  const filters = [
    { col: 'fiscal_year', op: 'in', val: selectedYears },
    { col: 'store_id', op: 'in', val: selectedStore },
  ];

  // Add channel filter if channels are provided
  if (channels && Array.isArray(channels) && channels.length > 0) {
    filters.push({ col: 'channel', op: 'IN', val: channels });
  }

  const payload = { [key]: { filters: filters } };
  return payload;
};

export const legendHighlightText = () => {
  return (
    <Typography variant="body2" sx={{ textAlign: 'center', padding: 2 }}>
      {LEGENDS_HIGHLIGHT_TEXT}
    </Typography>
  );
};

export const filterData = ({ rowData, currentYear, actualKey, plannedKey, dataKey }) =>
  rowData?.filter((item) => {
    // Check if the item corresponds to "Actual Spend"
    if (item[dataKey].includes(actualKey)) {
      return true; // Always include actual spend data
    }

    // Check if the item corresponds to "Planned Spend" for the current year
    if (item[dataKey].includes(plannedKey)) {
      const yearInLabel = item[dataKey].split(' ')[0]; // Extract year from the label
      return yearInLabel === `${currentYear}`; // Only include planned spend for the current year
    }

    return false; // Exclude everything else
  });

export const getSpendAndOppsFileName = ({ spendToggleState, viewType }) => {
  const isLocal = spendToggleState;
  const isMonthly = viewType === 'Monthly';

  if (isLocal && isMonthly) return SPEND_AND_OPPS_FILE_NAME_LOCAL_SPEND_MONTHLY;
  if (isLocal && !isMonthly) return SPEND_AND_OPPS_FILE_NAME_LOCAL_SPEND_WEEKLY;
  if (!isLocal && isMonthly) return SPEND_AND_OPPS_FILE_NAME_MONTHLY;

  return SPEND_AND_OPPS_FILE_NAME_WEEKLY;
};
